import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type Dispatch, type FC, type SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemTabs } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { INK_BLACK } from "../../../../theme/tokens";
import type { DayOff, NurseWithExternalData } from "../../../../utils/models";
import { DaysOffAndPTO } from "../DaysOffAndPTO/DaysOffAndPTO";
import { LastCallIns } from "../LastCallIns/LastCallIns";
import { SkeletonWithRandomWidths } from "../util/constants";
import { WeeklySchedule } from "../WeeklySchedule/WeeklySchedule";

type Props = {
	nurse: NurseWithExternalData;
	setNurseData: Dispatch<SetStateAction<NurseWithExternalData>>;
	onDeletePtoConfirmed: (dayOff: DayOff, restoreRecurringShifts: boolean, restoreTrimmedShifts: boolean) => Promise<void>;
	isDeletingTimeOff: boolean;
	isLoadingProfile: boolean;
};

type ScheduleTabValue = "all" | "schedule" | "time-off" | "call-in";

const innerSectionSx = {
	border: `1px solid ${alpha(INK_BLACK, 0.1)}`,
	borderRadius: "8px",
	padding: "12px 16px",
} as const;

export const ScheduleSection: FC<Props> = ({ nurse, setNurseData, onDeletePtoConfirmed, isDeletingTimeOff, isLoadingProfile }) => {
	const { t } = useTranslation();
	const { isMedicalDepartment } = useDepartmentFlags();
	const [scheduleTab, setScheduleTab] = useState<ScheduleTabValue>("all");

	const scheduleTabs: SystemTab<ScheduleTabValue>[] = [
		{ label: t("All tabs"), value: "all", icon: <ViewAgendaOutlinedIcon fontSize="small" /> },
		{ label: t("weekly.shift.schedule"), value: "schedule", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
		{ label: t("time.off"), value: "time-off", icon: <BeachAccessOutlinedIcon fontSize="small" /> },
		...(isMedicalDepartment ? [] : [{ label: t("call.ins"), value: "call-in" as const, icon: <PhoneOutlinedIcon fontSize="small" /> }]),
	];

	const weekly = isLoadingProfile ? <SkeletonWithRandomWidths header randomLines /> : <WeeklySchedule nurse={nurse} />;
	const timeOff = isLoadingProfile ? (
		<SkeletonWithRandomWidths card />
	) : (
		<DaysOffAndPTO setNurseData={setNurseData} nurse={nurse} onDeletePtoConfirmed={onDeletePtoConfirmed} isLoading={isDeletingTimeOff} />
	);
	const callIns = isLoadingProfile ? <SkeletonWithRandomWidths card /> : <LastCallIns nurse={nurse} />;

	return (
		<>
			<Box sx={{ display: "flex", justifyContent: "flex-end", borderBottom: "1px solid #ECEFF1" }}>
				{isLoadingProfile ? (
					<Box sx={{ width: "300px" }}>
						<SkeletonWithRandomWidths randomLines={true} contentDirection={"row"} />
					</Box>
				) : (
					<SystemTabs tabs={scheduleTabs} currentTab={scheduleTab} onTabChange={setScheduleTab} dataTestId="profile-schedule-tabs" />
				)}
			</Box>
			{scheduleTab === "all" && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Box sx={innerSectionSx}>{weekly}</Box>
					<Box sx={innerSectionSx}>{timeOff}</Box>
					{!isMedicalDepartment && <Box sx={innerSectionSx}>{callIns}</Box>}
				</Box>
			)}
			{scheduleTab === "schedule" && weekly}
			{scheduleTab === "time-off" && timeOff}
			{scheduleTab === "call-in" && !isMedicalDepartment && callIns}
		</>
	);
};
